// ===========================================
// Family Payments API Functions
// ===========================================
// The admin family-details "Payment history" section: invoice payments merged
// with the family's one-off registration fee, plus recording/amending/deleting
// an offline registration payment and waiving/revoking the fee.
//
// Family payment history, 2026-09-27. md/plan/family-payment-history.md Task 4.1.
// Types below mirror `services/registrationPayment/index.ts` field-by-field
// (CLAUDE.md: "a frontend response type is ASSERTED, never validated" — the
// backend type is the source of truth; diff these whenever that file changes).

import { apiClient } from '../services/api';
import type { PaymentMethod } from './invoices.api';

// ===========================================
// TYPES
// ===========================================

export interface RecordRegistrationPaymentInput {
  amount: number;
  method: Exclude<PaymentMethod, 'STRIPE'>;
  reference?: string;
  notes?: string;
  /** School-local calendar day (YYYY-MM-DD) the payment was received; omitted = today. */
  paidAt?: string;
  /** #374: the child the fee is for. Required by the server when the family has 2+ active children. */
  studentId?: string;
}

/** Amend a manual registration payment. Omitted fields are unchanged; '' clears reference/notes. */
export interface UpdateRegistrationPaymentInput {
  amount?: number;
  method?: Exclude<PaymentMethod, 'STRIPE'>;
  reference?: string;
  notes?: string;
  paidAt?: string;
}

/** The Convert dialog's registration-fee outcome (md/plan L5). */
export type AdminConvertRegistrationFee =
  | { mode: 'PAID_ONLINE' }
  | {
      mode: 'PAID_OTHER';
      amount: number;
      method: Exclude<PaymentMethod, 'STRIPE'>;
      paidAt?: string;
      reference?: string;
      notes?: string;
    }
  | { mode: 'NOT_COLLECTED' }
  | { mode: 'WAIVED'; reason: string };

export type FamilyPaymentRow = {
  kind: 'INVOICE' | 'REGISTRATION';
  id: string;
  amount: string; // "60.00"
  /** #210: a passed-on card fee charged on top of `amount` (invoice rows only); null when none. */
  processingFee: string | null;
  method: PaymentMethod;
  paidAt: string; // ISO instant — render the DAY only (noon placeholder)
  createdAt: string;
  reference: string | null;
  notes: string | null;
  isManual: boolean; // false for STRIPE / any stripe id
  invoice: {
    id: string;
    invoiceNumber: string;
    description: string | null;
    status: string;
    termName: string | null;
  } | null;
};

export type RegistrationFeeState = 'HELD' | 'APPLIED' | 'FORFEITED' | 'NOT_CREDITED' | 'UNASSIGNED';

/** #374: one paid fee of the family and what became of it. `fees` is [] without registration_fees.read. */
export type RegistrationFeeView = {
  id: string;
  amount: string; // "50.00"
  method: PaymentMethod;
  paidAt: string; // ISO instant: render the DAY only
  createdAt: string;
  reference: string | null;
  notes: string | null;
  isManual: boolean;
  student: { id: string; name: string } | null;
  /** False for a withdrawn, removed or inactive child: the fee is not credited (/qa 374 L3). */
  childActive?: boolean;
  state: RegistrationFeeState;
  /** How the child was matched when the fee was linked; ONLY_CHILD = the names differ (worth a check). */
  studentMatch: 'FULL_NAME' | 'FIRST_NAME' | 'ONLY_CHILD' | null;
  credit: { invoiceId: string; invoiceNumber: string; creditedAmount: string; worth: string } | null;
};

export type FamilyPaymentHistory = {
  familyId: string;
  year: number;
  years: number[];
  yearTotal: string;
  defaultRegistrationFee: string; // "50.00"
  payments: FamilyPaymentRow[];
  registration: {
    status: 'PAID' | 'WAIVED' | 'NONE';
    payment: FamilyPaymentRow | null;
    waiver: { waivedAt: string; waivedByName: string | null; reason: string } | null;
    viaMeetAndGreet: boolean;
    legacyAdminAttested: boolean;
    fees: RegistrationFeeView[];
    children: { id: string; name: string }[];
    scope: 'PER_FAMILY' | 'PER_STUDENT';
    /** null for a viewer without registration_fees.read. */
    creditOnFirstInvoice: boolean | null;
  };
  /** #372: the family's late-fee exemption (staff-only), or null. `at` is an instant. */
  lateFeeExemption?: { at: string; byName: string | null; reason: string } | null;
};

// ===========================================
// API RESPONSE TYPE
// ===========================================

interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

// ===========================================
// API
// ===========================================

export const familyPaymentsApi = {
  /**
   * GET /invoices/admin/families/:familyId/payments?year=YYYY
   * Omit `year` for the default (current school year) — never send an empty `?year=`.
   */
  getHistory: (familyId: string, year?: number): Promise<FamilyPaymentHistory> =>
    apiClient
      .get<ApiResponse<FamilyPaymentHistory>>(
        `/invoices/admin/families/${familyId}/payments`,
        year === undefined ? undefined : { params: { year } }
      )
      .then((res) => res.data),

  /** POST — record the registration fee as paid another way (offline). 201 on success. */
  recordRegistrationPayment: (
    familyId: string,
    data: RecordRegistrationPaymentInput
  ): Promise<FamilyPaymentHistory> =>
    apiClient
      .post<ApiResponse<FamilyPaymentHistory>>(
        `/invoices/admin/families/${familyId}/registration-payments`,
        data
      )
      .then((res) => res.data),

  /** PATCH — amend a MANUAL registration payment. Stripe rows are refused (400). */
  updateRegistrationPayment: (
    id: string,
    data: UpdateRegistrationPaymentInput
  ): Promise<FamilyPaymentHistory> =>
    apiClient
      .patch<ApiResponse<FamilyPaymentHistory>>(`/invoices/admin/registration-payments/${id}`, data)
      .then((res) => res.data),

  /** DELETE — a MANUAL registration payment (e.g. recorded against the wrong family). */
  deleteRegistrationPayment: (id: string): Promise<FamilyPaymentHistory> =>
    apiClient
      .delete<ApiResponse<FamilyPaymentHistory>>(`/invoices/admin/registration-payments/${id}`)
      .then((res) => res.data),

  /** PATCH (#374) — assign a paid fee (response is null without operations_invoices.read; never read it as a history) to the child it was for. 409 REGISTRATION_FEE_CREDITED / STUDENT_ALREADY_HAS_FEE. */
  assignRegistrationPaymentStudent: (
    familyId: string,
    id: string,
    studentId: string
  ): Promise<FamilyPaymentHistory | null> =>
    apiClient
      .patch<ApiResponse<FamilyPaymentHistory | null>>(
        `/invoices/admin/families/${familyId}/registration-payments/${id}/student`,
        { studentId }
      )
      .then((res) => res.data),

  /** POST — waive the registration fee (reason required). Refused when already paid or waived. */
  waiveRegistrationFee: (familyId: string, reason: string): Promise<FamilyPaymentHistory> =>
    apiClient
      .post<ApiResponse<FamilyPaymentHistory>>(
        `/invoices/admin/families/${familyId}/registration-fee-waiver`,
        { reason }
      )
      .then((res) => res.data),

  /** POST — revoke a waiver (optional reason, kept in the audit row). */
  revokeRegistrationFeeWaiver: (familyId: string, reason?: string): Promise<FamilyPaymentHistory> =>
    apiClient
      .post<ApiResponse<FamilyPaymentHistory>>(
        `/invoices/admin/families/${familyId}/registration-fee-waiver/revoke`,
        reason === undefined ? {} : { reason }
      )
      .then((res) => res.data),

  /** #372: exempt the family from late fees (reason required). Reminders still go. */
  setLateFeeExemption: (familyId: string, reason: string): Promise<FamilyPaymentHistory> =>
    apiClient
      .post<ApiResponse<FamilyPaymentHistory>>(`/invoices/admin/families/${familyId}/late-fee-exemption`, { reason })
      .then((res) => res.data),

  /** #372: clear the exemption (no reason needed; the audit keeps what was cleared). */
  clearLateFeeExemption: (familyId: string): Promise<FamilyPaymentHistory> =>
    apiClient
      .post<ApiResponse<FamilyPaymentHistory>>(`/invoices/admin/families/${familyId}/late-fee-exemption/clear`, {})
      .then((res) => res.data),
};
